/**
 * Minha conta (spec 019, 3.4; Vitor, 21/09, 008 secao 13, decisao 2: a aba "Minha assinatura" vira "Minha conta"): a
 * conta na corretora de cada plano vivo, com o numero (copiar), a etapa, o estado que importa pro cliente, o saldo com a
 * idade, o saldo inicial e o resultado. E o que o cliente da Global acompanha de verdade.
 *
 * O mesmo bloco do cartao do plano (`corretora.tsx`), sem o resto do plano (beneficios, regulamento), que mora em
 * Situacao. Acende pelo modulo `saldo` ou `nelogica-v2`, nunca pela marca. Tudo vem pronto do backend.
 */
import { Aviso, BotaoLink, CartaoDePlano, EsqueletoDeCartao, Grade, Vazio, formatarData, mensagemDe, type Fato } from "@mtc/ui";
import { ContaNaCorretoraDoCliente, contaDoPlanoDaGlobal, etapaDaGlobal } from "../corretora";
import { usePlanoAtual } from "../planoAtual";
import { STATUS_DA_MATRICULA, type Matricula } from "../tipos";
import { useMatriculas } from "./Planos";

/** Planos com conta pra acompanhar: os ativos, os suspensos e os que ainda esperam a conta. Encerrado fica em Situacao. */
const VIVOS = new Set<Matricula["status"]>(["ATIVA", "SUSPENSA", "AGUARDANDO_INICIO"]);

export function PaginaMinhaConta() {
  const q = useMatriculas();
  const { atual } = usePlanoAtual();
  if (q.isLoading) return <EsqueletoDeCartao linhas={3} rotulo="Buscando sua conta" />;
  if (q.isError) return <Aviso tipo="erro">{mensagemDe(q.error, "Não conseguimos buscar sua conta agora. Recarregue a página.")}</Aviso>;
  const todos = q.data?.itens ?? [];
  // O plano atual (o do seletor do menu) vem primeiro; os outros vivos, embaixo.
  const vivos = todos.filter((m) => VIVOS.has(m.status)).sort((a, b) => (a.id === atual?.id ? -1 : b.id === atual?.id ? 1 : 0));
  if (!vivos.length) {
    return (
      <Vazio
        titulo={todos.length ? "Nenhum plano em andamento" : "Você ainda não tem uma conta na corretora"}
        acao={todos.length ? <BotaoLink href="/planos" variante="secundario">Ver meus planos</BotaoLink> : <BotaoLink href="/comprar" variante="secundario">Ver planos à venda</BotaoLink>}
      >
        {todos.length ? "Seus planos encerrados continuam em Situação, pra consulta. A conta e o saldo aparecem aqui quando um plano estiver em andamento." : "Quando você comprar um plano, a conta na corretora e o saldo aparecem aqui."}
      </Vazio>
    );
  }
  return (
    <Grade minimo={360}>
      {vivos.map((m) => <ContaDoPlano key={m.id} m={m} destaque={vivos.length > 1 && m.id === atual?.id} />)}
    </Grade>
  );
}

function ContaDoPlano({ m, destaque }: { m: Matricula; destaque: boolean }) {
  const fatos: Fato[] = [...etapaDaGlobal(m), ...(m.ativada_em ? [{ rotulo: "Liberada em", valor: formatarData(m.ativada_em) }] : [])];
  return (
    <CartaoDePlano
      id={`conta-${m.id}`}
      titulo={m.plano.nome}
      subtitulo={m.comprada_em ? `Comprado em ${formatarData(m.comprada_em)}` : undefined}
      status={STATUS_DA_MATRICULA[m.status]}
      conta={contaDoPlanoDaGlobal(m)}
      saldo={<ContaNaCorretoraDoCliente m={m} />}
      fatos={fatos}
      passo={m.proximo_passo ? { titulo: m.proximo_passo.titulo, texto: m.proximo_passo.texto, tom: "info" } : undefined}
      acoes={<BotaoLink href={`/planos/${m.id}`} variante="fantasma" tamanho="pequeno">Ver o plano completo</BotaoLink>}
      destaque={destaque}
    />
  );
}
